<template>
    <div class="spo-card rounded bordered p-3 mb-4">
        <div class="text-body-2 mb-3">La clôture du groupe permet de désactiver et de bloquer les nouvelles inscriptions
            via ce lien.
        </div>
        <template v-if="isGroupePaid">
            <v-alert color="success">
                Le groupe est déjà clôturé et payé.
                <span v-if="groupe.total_responsable > 0">({{ groupe.total_responsable_with_tax }}€ par
                    {{typePaiementItems.find(item => item.value === groupe.type_paiement)?.label}})</span>
            </v-alert>
        </template>
        <v-form ref="formRef" @submit.prevent="onFormSubmit" v-if="!isGroupePaid && !paymentLinkGenerated">
            <div v-if="totalResponsableInitial === 0" class="d-flex flex-column ga-3 mb-3">
                <v-alert variant="tonal" color="info" density="comfortable">
                    Aucun montant n’a été calculé automatiquement pour le responsable. Vous pouvez clôturer sans
                    paiement, ou saisir ci-dessous les montants (chèque / virement à l’organisateur ou paiement en ligne
                    sur Sportips).
                </v-alert>
                <SportipsButton @onClick="openConfirmDialog('zero')" :loading="loading" action-button icon="mdi-check">
                    Clôturer sans paiement
                </SportipsButton>
            </div>

            <!-- Montants et type de paiement (y compris saisie manuelle si total initial = 0) -->
            <div class="d-flex flex-column ga-3">
                <div v-if="totalResponsableInitial > 0" class="text-body-2">Des inscriptions sont à la charge du
                    responsable. Les montants à régler
                    sont
                    calculés en fonction des inscriptions présentes. Vous pouvez modifier les montants à régler.</div>
                <div v-else class="text-body-2">Indiquez les montants à la charge du responsable (par défaut 0&nbsp;€) :
                    obligatoire pour enregistrer un paiement organisateur (chèque ou virement) ou une commande en ligne.
                </div>
                <div class="text-body-2 mb-3">Le paiement en ligne sur Sportips donnera lieu a la création d'une
                    commande dont
                    il faudra
                    communiquer le
                    lien de paiement au responsable du groupe.</div>
                <v-row>
                    <v-col cols="12" md="4">
                        <label class="field_label">Total parcours (€)</label>
                        <v-text-field v-model.number="totalParcours" type="number" min="0" step="0.01"
                            :rules="[v => v >= 0 || 'Le montant doit être positif']" hide-details="auto"
                            density="compact"></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                        <label class="field_label">Total options (€)</label>
                        <v-text-field v-model.number="totalOptions" type="number" min="0" step="0.01"
                            :rules="[v => v >= 0 || 'Le montant doit être positif']" hide-details="auto"
                            density="compact"></v-text-field>
                    </v-col>


                </v-row>
                <v-row>
                    <v-col cols="12" md="4">
                        <label class="field_label">Sous total à la charge du responsable (€)</label>
                        <div class="font-weight-bold text-h6">{{ totalResponsable.toFixed(2) }}€</div>
                    </v-col>
                    <v-col cols="12" md="4">
                        <label class="field_label">Taxe (€)</label>
                        <div class="font-weight-bold text-h6">{{ tax.toFixed(2) }}€</div>
                    </v-col>
                    <v-col cols="12" md="4">
                        <label class="field_label">Total à régler (€)</label>
                        <div class="font-weight-bold text-h6 text-primary-dark">{{ totalResponsableWithTax.toFixed(2)
                        }}€</div>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12" md="4">
                        <label class="field_label">Type de paiement</label>
                        <v-select v-model="typePaiement" :items="typePaiementItems" item-title="label"
                            item-value="value" hide-details density="compact"></v-select>
                    </v-col>
                    <v-col cols="12" md="4" v-if="typePaiement !== 'en_ligne'">
                        <label class="field_label">Statut du paiement</label>
                        <v-select v-model="statutPaiement" :items="statutPaiementItems" item-title="label"
                            item-value="value" hide-details density="compact"></v-select>
                    </v-col>

                </v-row>

                <v-alert v-if="submitError" type="error" variant="tonal" density="compact">{{ submitError }}</v-alert>
                <SportipsButton v-if="!isGroupePaid" @onClick="openConfirmDialog('submit')" :loading="loading"
                    action-button icon="mdi-check">Clôturer le groupe</SportipsButton>
            </div>
        </v-form>
        <template v-if="!isGroupePaid && paymentLinkGenerated">
            <div class="text-body-2">Le lien de paiement a été créé pour le paiement en ligne de la commande. Il est
                disponible ci-dessous :</div>
            <div class="d-flex align-center ga-2 mt-2">
                <SportipsButton @onClick="generate_payment_link" icon="mdi-link" action-button>Générer le lien de
                    paiement</SportipsButton>
            </div>
        </template>
        <v-dialog v-model="confirmDialog.show" width="500" max-width="100%" scroll-strategy="none" persistent>
            <v-card>
                <v-card-title>Confirmer la clôture</v-card-title>
                <v-card-text>
                    <div class="text-body-1">{{ confirmDialog.message }}</div>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="grey" variant="text" @click="closeConfirmDialog"
                        :disabled="confirmDialog.loading">Annuler</v-btn>
                    <v-btn color="primary" @click="confirmClose" :loading="confirmDialog.loading">
                        Confirmer
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapStores } from "pinia";
import { useMainStore } from "@/stores/mainStore";
import AdminService from "@/services/admin.service";
import TaxService from "@/services/booking/tax.service";

const TYPE_PAIEMENT_ITEMS = [
    { label: 'Chèque à l\'organisateur', value: 'cheque' },
    { label: 'Virement à l\'organisateur', value: 'virement' },
    { label: 'En ligne sur Sportips', value: 'en_ligne' },
];

const STATUT_PAIEMENT_ITEMS = [
    { label: 'En attente', value: 'UNPAID' },
    { label: 'Payé', value: 'PAID' },
];

export default {
    props: {
        groupe: { type: Object, required: true },
        totaux: { type: Object, default: () => ({}) },
        details: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            totalParcours: 0,
            totalOptions: 0,
            typePaiement: 'cheque',
            statutPaiement: 'UNPAID',
            tax: 0,
            loading: false,
            submitError: null,
            confirmDialog: {
                show: false,
                loading: false,
                message: '',
                pendingAction: null,
            },
        };
    },
    computed: {
        ...mapStores(useMainStore),
        totalResponsableInitial() {
            const parcours = this.totaux?.total_parcours != null ? parseFloat(this.totaux.total_parcours) : this.computeTotalParcoursFromDetails();
            const options = this.totaux?.total_options != null ? parseFloat(this.totaux.total_options) : this.computeTotalOptionsFromDetails();
            if (this.totaux?.total_responsable != null && this.totaux?.total_responsable !== undefined) {
                return parseFloat(this.totaux.total_responsable) || 0;
            }
            return (parcours || 0) + (options || 0);
        },
        totalParcoursInitial() {
            if (this.totaux?.total_parcours != null && this.totaux?.total_parcours !== undefined) {
                return parseFloat(this.totaux.total_parcours) || 0;
            }
            return this.computeTotalParcoursFromDetails();
        },
        totalOptionsInitial() {
            if (this.totaux?.total_options != null && this.totaux?.total_options !== undefined) {
                return parseFloat(this.totaux.total_options) || 0;
            }
            return this.computeTotalOptionsFromDetails();
        },
        totalResponsable() {
            return (parseFloat(this.totalParcours) || 0) + (parseFloat(this.totalOptions) || 0);
        },
        totalResponsableWithTax() {
            return this.totalResponsable + this.tax;
        },
        typePaiementItems() {
            return TYPE_PAIEMENT_ITEMS;
        },
        statutPaiementItems() {
            return STATUT_PAIEMENT_ITEMS;
        },
        isGroupePaid() {
            return this.groupe?.statut_paiement === 'PAID' || this.groupe?.statut_paiement === 'paye';
        },
        paymentLinkGenerated() {
            return this.groupe?.commande_uuid != null;
        }
    },
    watch: {
        totalParcoursInitial: {
            immediate: true,
            handler(val) {
                this.totalParcours = val;
            },
        },
        totalOptionsInitial: {
            immediate: true,
            handler(val) {
                this.totalOptions = val;
            },
        },
        totalResponsable: {
            handler() {
                this.updateTax();
            },
        },
        typePaiement() {
            this.updateTax();
        },
    },
    methods: {
        async generate_payment_link() {
            let request = await AdminService.generatePaymentLink(this.groupe.commande_uuid);
            if (request) {
                navigator.clipboard.writeText("https://sportips.fr/inscription/" + this.mainStore.module.base +
                    "/resend-payment?c=" + request.crypted_uuid);
                this.mainStore.notification.send("Lien copié dans le presse-papier", "success");
            }
            else {
                this.mainStore.notification.send("Erreur lors de la génération du lien", "error");
            }
        },
        computeTotalParcoursFromDetails() {
            let total = 0;
            if (!this.details || typeof this.details !== 'object') return total;
            for (const uuid in this.details) {
                const coureurs = this.details[uuid];
                if (Array.isArray(coureurs)) {
                    for (const coureur of coureurs) {
                        total += parseFloat(coureur.prix_inscription_responsable || 0);
                    }
                }
            }
            return parseFloat(total.toFixed(2));
        },
        computeTotalOptionsFromDetails() {
            let total = 0;
            if (!this.details || typeof this.details !== 'object') return total;
            for (const uuid in this.details) {
                const coureurs = this.details[uuid];
                if (Array.isArray(coureurs)) {
                    for (const coureur of coureurs) {
                        total += parseFloat(coureur.prix_options_responsable || 0);
                    }
                }
            }
            return parseFloat(total.toFixed(2));
        },
        async updateTax() {
            const total = parseFloat(this.totalResponsable) || 0;
            if (this.mainStore.module?.has_exclude_taxe) {
                this.tax = 0;
            } else {
                this.tax = await TaxService.computeTax(total, this.mainStore.module?.id, null);
            }
        },
        onFormSubmit(e) {
            e.preventDefault();
            this.openConfirmDialog('submit');
        },
        async openConfirmDialog(action) {
            this.submitError = null;
            if (action === 'submit') {
                const formValid = await this.$refs.formRef?.validate?.();
                if (formValid && formValid.valid === false) {
                    return;
                }
                if (this.totalResponsableInitial === 0 && this.totalResponsable <= 0) {
                    this.submitError =
                        'Saisissez un montant (parcours et/ou options), ou utilisez « Clôturer sans paiement ».';
                    return;
                }
            }
            this.confirmDialog.pendingAction = action;
            if (action === 'zero') {
                this.confirmDialog.message =
                    'Voulez-vous confirmer la clôture du groupe ? Aucun montant à régler pour le responsable.';
            } else {
                const total = this.totalResponsableWithTax;
                const typeLabel = this.typePaiementItems.find(i => i.value === this.typePaiement)?.label || '';
                this.confirmDialog.message =
                    `Voulez-vous confirmer la clôture du groupe ? Montant TTC : ${total}€ (${typeLabel}).`;
            }
            this.confirmDialog.show = true;
        },
        closeConfirmDialog() {
            this.confirmDialog.show = false;
            this.confirmDialog.loading = false;
            this.confirmDialog.pendingAction = null;
        },
        async confirmClose() {
            if (!this.confirmDialog.pendingAction) return;
            this.confirmDialog.loading = true;
            try {
                if (this.confirmDialog.pendingAction === 'zero') {
                    await this.submitZero();
                } else {
                    await this.submit();
                }
            } finally {
                this.confirmDialog.loading = false;
                this.closeConfirmDialog();
            }
        },
        async submitZero() {
            await this.doSubmit({
                disable: '1',
                total_responsable: 0,
                total_responsable_with_tax: 0,
                statut_paiement: 'PAID',
                type_paiement: 'aucun',
            });
        },
        async submit() {
            if (this.totalResponsable === 0) {
                if (this.totalResponsableInitial > 0) {
                    await this.submitZero();
                    return;
                }
                this.submitError =
                    'Saisissez un montant à la charge du responsable, ou utilisez « Clôturer sans paiement ».';
                return;
            }
            const total = this.totalResponsable;
            const closureData = {
                disable: '1',
                total_responsable: total,
                total_parcours: parseFloat(this.totalParcours) || 0,
                total_options: parseFloat(this.totalOptions) || 0,
                type_paiement: this.typePaiement,
                statut_paiement: this.statutPaiement,
                tax: this.tax,
                total_responsable_with_tax: this.totalResponsableWithTax,
            };
            await this.doSubmit(closureData);
        },
        async doSubmit(closureData) {
            this.loading = true;
            this.submitError = null;
            try {
                const res = await AdminService.closeLienGroupe(this.groupe.id, closureData);
                if (res === false) {
                    this.submitError = "Une erreur est survenue lors de la clôture du groupe.";
                    return;
                }
                this.mainStore.notification.send("Le groupe a été clôturé avec succès.", "success");
                this.$emit("onClose");
            } catch (err) {
                this.submitError = err?.message || "Une erreur est survenue lors de la clôture du groupe.";
            } finally {
                this.loading = false;
            }
        },
    },
    async mounted() {
        await this.updateTax();
        if (this.groupe?.disable === '1') {
            this.statutPaiement = this.groupe.statut_paiement;
            this.typePaiement = this.groupe.type_paiement;
            if (this.groupe.total_parcours != null || this.groupe.total_options != null) {
                this.totalParcours = parseFloat(this.groupe.total_parcours) || 0;
                this.totalOptions = parseFloat(this.groupe.total_options) || 0;
            } else {
                this.totalParcours = parseFloat(this.groupe.total_responsable) || 0;
                this.totalOptions = 0;
            }
        }
    },
};
</script>

<style lang="scss" scoped></style>
